import { ParsedUrlQuery } from 'querystring';

/**
 * Common parameters that are typically part of the path and should be omitted from query parameters.
 * These parameters are commonly used in URL paths and should not be duplicated in query parameters.
 *
 * @example
 * // URL: /en/trips/123/live-boarding-pass?bid=123&language=en
 * // bid and language are already in the path, so they should be omitted from query params
 */
export const COMMON_PATH_PARAMS = ['bid', 'language', 'help', 'tripId', 'offerId'] as const;

type CommonPathParams = (typeof COMMON_PATH_PARAMS)[number];

/**
 * Utility function to omit common path parameters and any additional specified parameters from a query object.
 * This is commonly used when constructing URLs to prevent duplicating path parameters in query strings.
 *
 * @param query - The query object to process (typically from Next.js router.query)
 * @param additionalParamsToOmit - Optional array of additional parameter names to omit
 *
 * @returns A new query object with the common path parameters and any additional specified parameters omitted
 *
 * @example
 * ```typescript
 * const { query } = useRouter();
 * // URL: /en/trips/123/live-boarding-pass?bid=123&language=en&view=map
 *
 * const otherQueries = omitCommonPathParams(query);
 * // otherQueries = { view: 'map' }
 *
 * // With additional parameters to omit
 * const otherQueries = omitCommonPathParams(query, ['view']);
 * // otherQueries = {}
 * ```
 *
 * @remarks
 * - Creates a new object without modifying the original
 * - Only includes parameters that are not in the omit list
 * - Preserves type information of the remaining parameters
 * - Commonly used with Next.js router.query to clean up URL parameters
 * - Automatically omits common path parameters (bid, language, help, tripId, offerId)
 */
export const omitCommonPathParams = <T extends ParsedUrlQuery, P extends string = never>(
  query: T,
  additionalParamsToOmit: readonly P[] = [] as readonly P[],
): Omit<T, CommonPathParams | P> => {
  const result = {} as Record<string, string | string[]>;
  const paramsToOmit = [...COMMON_PATH_PARAMS, ...additionalParamsToOmit] as readonly string[];

  for (const [key, value] of Object.entries(query)) {
    if (!paramsToOmit.includes(key) && value !== undefined) {
      result[key] = value;
    }
  }

  return result as Omit<T, CommonPathParams | P>;
};
